import { createAsyncThunk } from '@reduxjs/toolkit';

import cachedFetch from '../../../../shared/cachedFetch';
import { timeInMs } from '../../../../shared';
import { Picture, UiState } from './types';
import {
  getFile,
  removeFile,
  requestFileSystem,
} from '../../../../shared/chromeApi';
import { isSameDay } from '../../../../shared';

import {
  checkImageResolution,
  getRandomVideo,
  getResolutionUrl,
  preloadPicture,
  preloadVideo,
} from './utils';
import { GlobalState, Thunk } from '../../../types.global';

import { selectCustomPictureUrl } from './selectors';
import {
  selectedNextPicture,
  preloadedNextPicture,
  selectedCollection,
  changedMode,
  setNextVideo,
} from './reducer';

import { DEFAULT_PICTURE } from './dict';

const APP_PLATFORM = process.env.APP_PLATFORM;

interface BgResponse {
  categories: { [key: string]: Picture[] };
  resolutions: number[][];
}

export const fetchBackgrounds = (): Promise<BgResponse> =>
  cachedFetch<BgResponse>(
    'https://assets.oceanhero.today/backgrounds.json',
    timeInMs.day
  ).catch((err) => {
    console.error('fetchBackgrounds', err);
    return null;
  });

export const fetchVideos = (): Promise<BgResponse> =>
  cachedFetch<BgResponse>(
    'https://assets.oceanhero.today/backgrounds/videos.json',
    timeInMs.day
  ).catch((err) => {
    console.error('fetchVideos', err);
    return null;
  });

const selectShouldPreloadNext = ({ ui }: { ui: UiState }): boolean => {
  const { image, rotation } = ui.gallery;

  if (rotation.type === 'byTabs') return image.viewCount >= rotation.value;

  if (rotation.type === 'byDays') return !isSameDay(image.updatedAt);

  return false;
};

const getNextPicture = (categories, category, picture): Picture => {
  const list = categories[category];

  if (!picture) return list[0];

  const index = list.findIndex(({ url }) => url === picture.originUrl);

  return list[index + 1] || list[0];
};

const preloadNextPicture =
  ({ backgrounds, picture }) =>
    async (dispatch, getState) => {
      const state = getState();

      const currentPicture = picture || state.ui.gallery.image.picture;

      const nextPicture = getNextPicture(
        backgrounds.categories,
        state.ui.gallery.category,
        currentPicture
      );

      const resolutionUrl = getResolutionUrl(
        window.screen.width,
        backgrounds.resolutions,
        nextPicture.url
      );

      dispatch(
        selectedNextPicture({
          ...nextPicture,
          url: resolutionUrl,
          originUrl: nextPicture.url,
        })
      );

      await preloadPicture(resolutionUrl);
      return dispatch(preloadedNextPicture());
    };

export const prepareNextPicture = (): Thunk => async (dispatch, getState) => {
  const state = getState();

  if (!selectShouldPreloadNext(state)) return;

  const backgrounds = await fetchBackgrounds();
  if (!backgrounds) return null;
  await dispatch(preloadNextPicture({ backgrounds, picture: null }));
};

export const prepareNextVideo = (): Thunk => async (dispatch, getState) => {
  const state = getState();

  const rotation = state.ui.gallery.rotation;
  const videoBackground = state.ui.videoBackground;

  if (!rotation.type) return;

  if (
    (rotation.type === 'byTabs' && !videoBackground?.nextVideo) ||
    (rotation.type === 'byDays' && !isSameDay(videoBackground.updatedAt))
  ) {
    // console.log('prepareNextVideo');
    const video = await getRandomVideo(state.ui.videoBackground?.index || 0);

    if (!video) return;

    preloadVideo(video.url).then(() => {
      dispatch(setNextVideo({ ...video, index: video.newIndex }));
    });
  }
};

export const setRawPicture = createAsyncThunk(
  'ui/setRawPicture',
  async ({
    file,
    name,
    size,
    screenWidth,
  }: {
    file: File;
    name: string;
    size: number;
    screenWidth: number;
  }): Promise<{ url: string; isSmallImage: boolean }> => {
    const fs = await requestFileSystem(size);

    const fileEntry = await getFile(fs, name, {
      create: true,
      exclusive: true,
    });

    const fileWriter: FileWriter = await new Promise((resolve, reject) =>
      fileEntry.createWriter(resolve, reject)
    );

    await new Promise((resolve, reject) => {
      fileWriter.onwriteend = resolve;
      fileWriter.onerror = reject;
      fileWriter.write(file);
    });

    return {
      url: fileEntry.toURL(),
      isSmallImage: await checkImageResolution(fileEntry.toURL(), screenWidth),
    };
  }
);

export const setCustomPicture = createAsyncThunk(
  'ui/setCustomPicture',
  async (currentTarget: EventTarget | DataTransfer, { dispatch }) => {
    // const state = getState() as GlobalState;
    const screenWidth = window.screen.width;
    // const errorHandler = (e: Error): void => {
    //   console.error('setCustomPicture', e);
    // };

    const file: File = (currentTarget as HTMLInputElement)?.files[0];
    const name = `${Date.now()}.${file.name.split('.').pop()}`;
    const size = Math.max(20000000, file.size);

    if (file.size > 20000000) {
      throw new Error('Maximum file size exceeded');
    }

    if (APP_PLATFORM === 'extension') {
      // const fs = await requestFileSystem(size);
      // const currentCustomPictureUrl = selectCustomPictureUrl(state);
      // if (currentCustomPictureUrl) {
      //   await removeFile(fs, currentCustomPictureUrl).catch(errorHandler);
      // }
      dispatch(setRawPicture({ file, name, size, screenWidth }));
    }
  }
);

export const selectOceanGallery = (): Thunk => (dispatch, getState) => {
  const state = getState();
  // const { category: activeCategory } = selectActiveView(state);

  dispatch(
    state.ui.gallery.category === 'ocean'
      ? changedMode({ mode: 'ocean' })
      : selectedCollection({
        rotation: { type: 'byTabs', value: 2, category: 'ocean' },
        category: 'ocean',
        now: Date.now(),
        picture: DEFAULT_PICTURE,
      })
  );
};
